import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  cleanupLegacyAuthState,
  clearCandidateSession,
  getCandidateUserInfo,
} from "../candidateSession";
import { validateCandidateSession } from "../validateCandidateSession";
import { useLogin } from "../hooks/useLogin";
import { useLogout } from "../hooks/useLogout";
import { UserInfo } from "../types/userInfo";

interface AuthContextInterface {
  hasRole: (roles?: string[]) => {};
  isLoggingIn: boolean;
  isLoggingOut: boolean;
  sessionReady: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<any>;
  create: (
    cpf: string,
    email: string,
    password: string,
    verifyPassword: string
  ) => Promise<any>;
  verify: (cpf: string) => Promise<any>;
  logout: () => Promise<any>;
  userInfo?: UserInfo;
}

export const AuthContext = createContext({} as AuthContextInterface);

type AuthProviderProps = {
  children?: React.ReactNode;
};

const AuthProvider = ({ children }: AuthProviderProps) => {
  const [sessionVersion, setSessionVersion] = useState(0);
  const [sessionReady, setSessionReady] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { isLoggingIn, login, verifyCandidate, create } = useLogin();
  const { isLoggingOut, logout } = useLogout();

  const bumpSession = useCallback(() => {
    setSessionVersion((version) => version + 1);
  }, []);

  const runSessionValidation = useCallback(async () => {
    cleanupLegacyAuthState();
    const valid = await validateCandidateSession();
    setIsAuthenticated(valid);
    bumpSession();
    return valid;
  }, [bumpSession]);

  useEffect(() => {
    let cancelled = false;

    runSessionValidation().finally(() => {
      if (!cancelled) {
        setSessionReady(true);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [runSessionValidation]);

  const userInfo = useMemo(() => {
    void sessionVersion;
    if (!isAuthenticated) {
      return undefined;
    }
    return getCandidateUserInfo();
  }, [sessionVersion, isAuthenticated]);

  const hasRole = (roles?: string[]) => {
    if (!roles || roles.length === 0) {
      return true;
    }
    if (!userInfo) {
      return false;
    }
    return roles.includes(userInfo.role);
  };

  const handleVerify = async (cpf: string) => {
    return verifyCandidate({ cpf })
      .then((result: any | boolean) => {
        return result;
      })
      .catch((err) => {
        throw err;
      });
  };

  const handleLogin = async (cpf: string, password: string) => {
    return login({ cpf, password })
      .then(async (ok: boolean) => {
        if (!ok) {
          return false;
        }
        const valid = await runSessionValidation();
        return valid;
      })
      .catch(() => false);
  };

  const handleCreate = async (
    cpf: string,
    email: string,
    password: string,
    verifyPassword: string
  ) => {
    return create({ cpf, password, email })
      .then((ok: boolean) => {
        if (!ok) {
          return false;
        }
        return true;
      })
      .catch(() => false);
  };

  const handleLogout = async () => {
    return logout()
      .then((data) => {
        clearCandidateSession();
        setIsAuthenticated(false);
        bumpSession();
        return data;
      })
      .catch((err) => {
        throw err;
      });
  };

  return (
    <AuthContext.Provider
      value={{
        hasRole,
        isLoggingIn,
        isLoggingOut,
        sessionReady,
        isAuthenticated,
        login: handleLogin,
        logout: handleLogout,
        verify: handleVerify,
        create: handleCreate,
        userInfo,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  return useContext(AuthContext);
}

export default AuthProvider;
